import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Mic, MicOff, Upload, Play, Pause, Trash2, Loader2, FileAudio, ChevronDown, ChevronUp, Pencil, Check, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceNotes({ projectId }) {
  const [notes, setNotes] = useState([]);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/projects/${projectId}/voice-notes`, { withCredentials: true });
    setNotes(data);
  }, [projectId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await uploadAudio(blob, `recording-${Date.now()}.webm`);
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied. Please allow microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const uploadAudio = async (blob, filename) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", blob, filename);
    fd.append("title", filename.replace(/\.[^.]+$/, "").replace(/-/g, " "));
    try {
      await axios.post(`${API}/admin/projects/${projectId}/voice-notes`, fd, { withCredentials: true });
      fetchNotes();
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadAudio(file, file.name);
    e.target.value = "";
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this voice note?")) return;
    await axios.delete(`${API}/admin/voice-notes/${id}`, { withCredentials: true });
    fetchNotes();
  };

  const retryTranscription = async (id) => {
    try {
      const note = notes.find(n => n.id === id);
      if (note) note.transcription_status = "pending";
      setNotes([...notes]);
      await axios.post(`${API}/admin/voice-notes/${id}/retry`, {}, { withCredentials: true });
      fetchNotes();
    } catch (err) {
      alert("Retry failed: " + (err.response?.data?.detail || err.message));
      fetchNotes();
    }
  };

  const playAudio = (noteId) => {
    if (playingId === noteId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(`${API}/admin/voice-notes/${noteId}/audio`);
    audio.onended = () => setPlayingId(null);
    audio.play();
    audioRef.current = audio;
    setPlayingId(noteId);
  };

  const saveTitle = async (noteId) => {
    await axios.put(`${API}/admin/voice-notes/${noteId}`, { title: editTitle }, { withCredentials: true });
    setEditingTitle(null);
    fetchNotes();
  };

  const formatDuration = (sec) => {
    if (!sec) return "";
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div data-testid="voice-notes">
      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-4">
        {!recording ? (
          <button onClick={startRecording} disabled={uploading} data-testid="start-recording-btn"
            className="flex items-center gap-1.5 bg-red-500/10 text-red-400 border border-red-500/30 px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-red-500/20 transition-all disabled:opacity-50">
            <Mic className="w-3.5 h-3.5" /> Record
          </button>
        ) : (
          <button onClick={stopRecording} data-testid="stop-recording-btn"
            className="flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-sm text-xs font-medium animate-pulse">
            <MicOff className="w-3.5 h-3.5" /> Stop recording
          </button>
        )}
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading || recording} data-testid="upload-audio-btn"
          className="flex items-center gap-1.5 bg-[#1E3050]/40 text-[#857973] border border-[#1E3050] px-3 py-1.5 rounded-sm text-xs hover:text-white transition-colors disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> Upload audio
        </button>
        <input ref={fileInputRef} type="file" accept="audio/*,.mp3,.wav,.webm,.m4a,.mp4,.mpeg,.mpga" onChange={handleFileUpload} className="hidden" />
        {uploading && (
          <span className="flex items-center gap-1.5 text-xs text-[#F59073]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing...
          </span>
        )}
      </div>

      {/* Notes list */}
      <div className="space-y-2">
        {notes.map(n => (
          <div key={n.id} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm overflow-hidden" data-testid={`note-${n.id}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#1E3050]/20 transition-colors"
              onClick={() => setExpandedNote(expandedNote === n.id ? null : n.id)}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <button onClick={(e) => { e.stopPropagation(); playAudio(n.id); }} className={`p-1.5 rounded-sm transition-colors ${playingId === n.id ? "bg-[#F59073]/10 text-[#F59073]" : "text-[#857973] hover:text-white"}`}>
                  {playingId === n.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <div className="min-w-0 flex-1">
                  {editingTitle === n.id ? (
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <input value={editTitle} onChange={e => setEditTitle(e.target.value)} onKeyDown={e => e.key === "Enter" && saveTitle(n.id)}
                        className="bg-[#0A1628] border border-[#1E3050] text-white px-2 py-0.5 rounded-sm text-sm focus:border-[#D9A872] outline-none flex-1" autoFocus />
                      <button onClick={() => saveTitle(n.id)} className="text-green-400"><Check className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <span className="text-sm text-white truncate block">{n.title}</span>
                  )}
                  <div className="flex items-center gap-2 text-xs text-[#857973]">
                    <span>{n.created_at?.slice(0, 10)}</span>
                    {n.duration && <span>{formatDuration(n.duration)}</span>}
                    {n.transcription_status === "pending" && <span className="text-yellow-400">transcribing...</span>}
                    {n.transcription_status === "failed" && <span className="text-red-400">failed</span>}
                    {n.transcription_status === "completed" && <span className="text-green-400">transcribed</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {n.transcription_status === "failed" && (
                  <button onClick={(e) => { e.stopPropagation(); retryTranscription(n.id); }} data-testid={`retry-${n.id}`}
                    className="flex items-center gap-1 text-[#F59073] hover:text-[#D9A872] transition-colors text-xs px-2 py-1 border border-[#F59073]/30 rounded-sm">
                    <RotateCcw className="w-3 h-3" /> Retry
                  </button>
                )}
                {n.transcription_status === "pending" && (
                  <span className="flex items-center gap-1 text-xs text-[#D9A872]"><Loader2 className="w-3 h-3 animate-spin" /> Transcribing</span>
                )}
                <button onClick={(e) => { e.stopPropagation(); setEditingTitle(n.id); setEditTitle(n.title); }} className="text-[#1E3050] hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); deleteNote(n.id); }} className="text-[#1E3050] hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                {expandedNote === n.id ? <ChevronUp className="w-4 h-4 text-[#857973]" /> : <ChevronDown className="w-4 h-4 text-[#857973]" />}
              </div>
            </div>

            {/* Expanded transcription */}
            {expandedNote === n.id && n.transcription && (
              <div className="px-4 pb-4 border-t border-[#1E3050]">
                <div className="mt-3">
                  <span className="font-mono text-[10px] text-[#857973] block mb-2">TRANSCRIPTION</span>
                  <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap font-manrope">{n.transcription}</p>
                </div>
                {n.segments?.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs text-[#857973] cursor-pointer hover:text-[#857973]">Show timestamps</summary>
                    <div className="mt-2 space-y-1">
                      {n.segments.map((s, i) => (
                        <div key={i} className="flex gap-2 text-xs">
                          <span className="text-[#857973] font-mono w-20 flex-shrink-0">{formatDuration(s.start)} → {formatDuration(s.end)}</span>
                          <span className="text-[#857973]">{s.text}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        ))}
        {notes.length === 0 && !uploading && (
          <div className="text-center py-8">
            <FileAudio className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
            <p className="text-[#857973] text-sm">No voice notes yet. Record or upload audio to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
